import { useMemo } from 'react';
import { Code, Workflow } from 'lucide-react';
import { SegmentedControl } from '../../../../components/ui/segmented-control';
import { useBetaFeature } from '../../../../core/beta';
import { useShortcutLabel } from '../../../keybindings/hooks/useShortcutLabel';
import type { QueryViewMode } from '../../core/types/workspaceTypes';

type QueryViewToggleProps = {
  value: QueryViewMode;
  onChange: (mode: QueryViewMode) => void;
};

/**
 * SQL | Visual — which view of this tab's SQL is shown. Both views edit the
 * same buffer. Icon-only segments in the toolbar's document group (a labelled
 * control tips the toolbar into its collapsed layout at ~1200 px). A
 * `visual-query-editor` beta surface; the `feature:vqe_mode_toggle` event
 * fires from the tab's `setViewMode`.
 */
export function QueryViewToggle({ value, onChange }: QueryViewToggleProps) {
  const enabled = useBetaFeature('visual-query-editor');
  const shortcut = useShortcutLabel('global.openVisualQueryEditor');
  const options = useMemo(
    () => [
      { value: 'sql' as const, label: <Code className="h-4 w-4" />, title: 'SQL view', testId: 'query-view-sql' },
      {
        value: 'visual' as const,
        label: <Workflow className="h-4 w-4" />,
        title: `Visual view — the statement under the caret as cards${shortcut ? ` (${shortcut})` : ''}`,
        testId: 'query-view-visual',
      },
    ],
    [shortcut],
  );
  if (!enabled) return null;
  return (
    <div className="flex shrink-0 items-center" data-toolbar-item>
      <SegmentedControl
        value={value}
        onValueChange={onChange}
        options={options}
        triggerClassName="px-2"
        aria-label="View"
        testId="query-view-toggle"
      />
    </div>
  );
}
